import { createClient } from '@supabase/supabase-js'

/**
 * THE Supabase client. There must be exactly one.
 *
 * Until 2026-10-05 there were two: this file, and src/supabaseClient.js. Both
 * called createClient() on the same env vars, which puts two GoTrueClient
 * instances in one browser context. They share a storage key, so they race:
 * each refreshes the access token on its own timer and writes the result over
 * the other's session.
 *
 * The split ran straight through authentication. Auth.jsx and
 * AuthCallback.jsx - the login page and the OAuth callback - used the OTHER
 * client, while AuthContext and twelve other modules read session state from
 * this one. A user signed in against one instance and the app asked a
 * different instance whether they were signed in. It works most of the time,
 * which is the worst property a bug can have.
 *
 * DO NOT call createClient anywhere else. Import this.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Warn rather than throw. The deleted client threw at module load, which
// white-screens the whole app with nothing but a console line - and it is
// what makes App.test.jsx fail in a clean checkout (recorded in
// BECIVITAS.md). Nothing is protected by the throw: the anon key is public
// and RLS-scoped, and with no config every query fails visibly anyway. A
// visible failure beats a blank page.
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase env vars missing — check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY. ' +
      'Database and auth calls will fail until these are set.'
  )
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

export async function adminListUsers() {
  const { data, error } = await supabase.rpc('admin_list_users')
  if (error) throw error
  return data
}

export async function adminSetUserRole(targetUserId, newRole) {
  const { error } = await supabase.rpc('admin_set_user_role', {
    target_user_id: targetUserId,
    new_role: newRole,
  })
  if (error) throw error
}

export default supabase
